import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler } from '../PlayerHandler';

interface UsernameHistoryProps {
  userId: string;
  onForceChange: () => void;
}

interface UsernameChangeItem {
  previousUsername?: string;
  newUsername?: string;
  changeDate?: string;
}

const UsernameHistory: React.FC<UsernameHistoryProps> = ({ userId, onForceChange }) => {
  const { theme, isMobile } = useTheme();
  const [history, setHistory] = useState<UsernameChangeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const handler = new PlayerHandler();
        const response = await handler.getUsernameHistory(userId);
        if (response?.success) {
          setHistory(response.history || []);
        }
      } catch (err) {
        console.error('Error loading username history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [userId]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '12px',
      padding: isMobile ? '16px' : '20px',
      marginTop: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
    },
    title: {
      fontSize: '16px',
      fontWeight: 700,
      color: theme.text,
    },
    badge: {
      fontSize: '12px',
      padding: '2px 8px',
      borderRadius: '10px',
      background: theme.tableHeader,
      color: theme.textSecondary,
    },
    chevron: {
      fontSize: '14px',
      color: theme.textSecondary,
      transition: 'transform 0.2s',
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    },
    content: {
      marginTop: '12px',
      display: expanded ? 'block' : 'none',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '8px 0',
      borderBottom: `1px solid ${theme.border}`,
    },
    arrow: {
      color: theme.textSecondary,
      fontSize: '14px',
    },
    oldName: {
      fontSize: '13px',
      color: theme.textSecondary,
      textDecoration: 'line-through' as const,
    },
    newName: {
      fontSize: '13px',
      fontWeight: 600,
      color: theme.text,
    },
    date: {
      fontSize: '11px',
      color: theme.textSecondary,
      marginLeft: 'auto',
    },
    empty: {
      fontSize: '13px',
      color: theme.textSecondary,
      fontStyle: 'italic' as const,
      padding: '8px 0',
    },
    forceBtn: {
      padding: '4px 12px',
      fontSize: '11px',
      fontWeight: 500,
      border: `1px solid ${theme.border}`,
      borderRadius: '6px',
      cursor: 'pointer',
      background: 'transparent',
      color: theme.text,
      marginLeft: '8px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header} onClick={() => setExpanded(!expanded)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={styles.title}>Username History</span>
          <span style={styles.badge}>{history.length}</span>
          <button style={styles.forceBtn} onClick={(e) => { e.stopPropagation(); onForceChange(); }}>
            Force Change
          </button>
        </div>
        <span style={styles.chevron}>{expanded ? '▲' : '▼'}</span>
      </div>
      <div style={styles.content}>
        {loading && <div style={styles.empty}>Loading...</div>}
        {!loading && history.length === 0 && <div style={styles.empty}>No username changes recorded</div>}
        {history.map((item, i) => (
          <div key={i} style={styles.row}>
            <span style={styles.oldName}>{item.previousUsername}</span>
            <span style={styles.arrow}>→</span>
            <span style={styles.newName}>{item.newUsername}</span>
            <span style={styles.date}>{formatDate(item.changeDate)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsernameHistory;
